import React from "react";
import { Link } from "react-router-dom";
import { PawPrint, MessageCircle, ArrowRight, Stethoscope, Syringe, Salad, Scissors, Home as HomeIcon, HeartPulse } from "lucide-react";
import Reveal from "../Reveal";

const services = [
  { icon: Stethoscope, title: "Wellness Check", desc: "Delivered by one of our experienced veterinary nurses, this provides a thorough review of your pet's health, along with personalised advice, practical guidance and recommendations tailored to their age, breed and lifestyle." },
  { icon: Syringe, title: "Vaccination Program", desc: "If the number of vaccinated pets decreases, our four-legged friends could be at risk from an outbreak of infectious diseases — some of which can be transmitted to humans. Keep your pets safe by keeping them up to date with their vaccinations." },
  { icon: Salad, title: "Nutritional Guidance", desc: "Pet owners are increasingly opting for non-traditional diets for their cats and dogs. We help you navigate raw, meat-free and alternative-protein diets with evidence-based advice tailored to your pet." },
  { icon: Scissors, title: "Grooming Session", desc: "If you're looking for the full grooming experience — a bath, clip and scissor — or a pampering session to tidy them up, we offer you and your pet a relaxed and enjoyable experience with our dog grooming facilities." },
  { icon: HomeIcon, title: "Pet Boarding", desc: "We run an award-winning business that is amongst the finest dog boarding kennels, and will look after your pet with such care that you won't have a worry in the world." },
  { icon: HeartPulse, title: "Rehabilitation Therapy", desc: "We believe good rehabilitation is integral to the success of many orthopaedic, neurological and soft tissue conditions. Intervention promotes mobility, reduces pain and inflammation, and supports the overall rate of recovery." },
];

const Services = () => {
  return (
    <section className="bg-pawelle-bg py-20 md:py-28">
      <div className="mx-auto max-w-[1290px] px-5">
        <div className="grid items-end gap-8 lg:grid-cols-2">
          <div>
            <Reveal animation="rubberBand">
              <span className="inline-flex items-center gap-2 font-display text-[15px] font-semibold text-pawelle-primary">
                <PawPrint className="h-5 w-5" />
                What we offer
              </span>
            </Reveal>
            <Reveal animation="rubberBand" delay={100}>
              <h2 className="mt-4 max-w-xl font-display text-[30px] font-bold leading-tight text-pawelle-ink md:text-[38px]">
                Compassionate Care Designed for Your Pet's Wellness
              </h2>
            </Reveal>
          </div>
          <Reveal animation="fadeInRight" delay={150}>
            <p className="font-body text-[16px] leading-relaxed text-pawelle-body">
              We aim to earn our place as a beating heart of the community. Being a small company ourselves, we will
              support local businesses and employ locally where we can. We will also support local charities and
              neighbourhood initiatives, and welcome visitors whose pets may need veterinary care. As a practice we
              will strive to improve sustainability, reducing waste and recycling where possible — and we aim to help
              educate local children in environmental awareness, including care and respect for animals and wildlife.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link
                to="/gb/en/appointment.html"
                className="inline-flex items-center gap-2 rounded-full bg-pawelle-primary px-6 py-3 font-display text-[15px] font-medium text-white transition-all duration-300 hover:bg-pawelle-primary-dark hover:-translate-y-0.5"
              >
                <MessageCircle className="h-4 w-4" />
                Free Consultations
              </Link>
              <Link
                to="/gb/en/services.html"
                className="inline-flex items-center gap-2 rounded-full border-2 border-pawelle-primary px-6 py-3 font-display text-[15px] font-medium text-pawelle-primary transition-all duration-300 hover:bg-pawelle-primary hover:text-white"
              >
                <ArrowRight className="h-4 w-4" />
                Discover More
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal
                key={s.title}
                animation="rubberBand"
                delay={i * 80}
                className="group rounded-[28px] bg-white p-8 shadow-[0_14px_40px_rgba(67,48,109,0.06)] transition-all duration-300 hover:-translate-y-2.5 hover:shadow-[0_24px_60px_rgba(114,82,155,0.18)]"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-pawelle-bg text-pawelle-primary transition-colors group-hover:bg-pawelle-primary group-hover:text-white">
                  <Icon className="h-8 w-8" />
                </div>
                <h3 className="mt-6 font-display text-[22px] font-semibold text-pawelle-ink">{s.title}</h3>
                <div className="my-5 h-px w-full bg-pawelle-ink/10" />
                <p className="font-body text-[15px] leading-relaxed text-pawelle-body">
                  {s.desc}
                </p>
                <Link
                  to="/gb/en/detail-service.html"
                  className="mt-5 inline-flex items-center gap-1 font-display text-[15px] font-medium text-pawelle-primary transition-all hover:gap-2"
                >
                  Learn More <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
